function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Callout, Tooltip, useOverlayContext } from 'seagull';
import { Page, Typography, Card, Grid, GridItem, Button, ConfirmationModal } from 'seagull';
import { useEventReporter } from "../../../event-reporter";
import { useStore, useStoreActions } from "../../../store/store-context";
import { useIntegration } from "../../../hooks/useIntegration";
import { openInNewTabProps, routes } from "../../../constants";
import { selectors } from "../../../store/state-selectors";
import { integrationModel } from "../../../models";
import { Intent } from "../../../infra/access-control";
import { MapServicesModal } from "../MapServicesModal/MapServicesModal";
import { MapContactsModal } from "../MapContactsModal/MapContactsModal";
import { MapAccountsModal } from "../MapAccountsModal/MapAccountsModal";
import ContactSupportButton from "../../../components/ContactSupportButton";
import ConditionalWrapper from "../../../components/ConditionalWrapper/ConditionalWrapper";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { isAccountingQbo } from "../../../types/api.model";
import { getAccountingDetails } from "../accounting.utils";
import { AccountingAdvancedSettings } from "../AccountingAdvancedSettings/AccountingAdvancedSettings";
import styles from "./AccountingSetupPage.module.scss";
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export function AccountingSetupPage(_ref) {
  let {
    accounting
  } = _ref;
  const {
    showModal
  } = useOverlayContext();
  const isPendingAccountingIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(accounting));
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const {
    disconnect: disconnectFromAccounting
  } = useIntegration(accounting);
  const history = useHistory();
  const accountingIntegration = useStore(state => selectors.integration.accountingIntegration(state, accounting));
  const [isLoading, setIsLoading] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const isLinked = accountingIntegration && integrationModel.isLinked(accountingIntegration);
  const {
    reportToAppcues,
    reportToGtm,
    reportToHeap,
    reportToHubspot
  } = useEventReporter();
  const {
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const {
    XERO_INTEGRATION
  } = useFeatureFlags();
  const {
    name: accountingName,
    biCodes,
    fullName: accountingFullName,
    helpArticle,
    testIdPrefix
  } = getAccountingDetails(accounting);
  const title = "".concat(accountingFullName, " Setup");
  const subheader = XERO_INTEGRATION ? "Manage your Anchor connection with ".concat(accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.companyName(), ".") : "Set up your ".concat(accountingFullName, " account connection.");
  useEffect(() => {
    setIsLoading(true);
    fetchIntegrations().then(integrations => {
      const accountingInteg = integrationModel.accountingIntegration(integrations, accounting);
      const hasIntegration = Boolean(accountingInteg);
      const isIntegrationLinkedAndNotReady = accountingInteg && accountingInteg.isLinked() && !accountingInteg.isReady();
      if (!hasIntegration || isIntegrationLinkedAndNotReady) history.push(routes.availableApps);
    }).finally(() => setIsLoading(false));
  }, []);
  const subHeader = useMemo(() => getSubHeaderByIntegration(accountingIntegration !== null && accountingIntegration !== void 0 ? accountingIntegration : undefined), [accountingIntegration]);
  const disconnect = async () => {
    const userApprovedDisconnect = await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          "data-testid": "".concat(testIdPrefix, "-disconnect-modal"),
          title: "Disconnect from ".concat(accountingFullName),
          content: /*#__PURE__*/_jsxs(Typography, {
            children: ["Are you sure you want to disconnect from ", accountingFullName, "?"]
          }),
          onCloseButtonClick: () => dismissModal(false),
          onPrimaryButtonClick: () => dismissModal(true),
          primaryButtonLabel: "Disconnect",
          onSecondaryButtonClick: () => dismissModal(false),
          secondaryButtonLabel: "Cancel",
          skin: "danger"
        });
      }
    });
    if (userApprovedDisconnect) {
      setIsDisconnecting(true);
      logBI(biCodes.disconnect, {
        vendorId,
        userId
      });
      if (isAccountingQbo(accounting)) {
        reportToAppcues('button-click', 'qbo-vendor-disconnect-connection');
        reportToGtm('button-click', 'qbo-vendor-disconnect-connection', {
          vendorId,
          userId
        });
        reportToHubspot('vendor_disconnects_qbo_connection');
      }
      await disconnectFromAccounting();
      setIsDisconnecting(false);
      await fetchIntegrations();
      history.push(routes.availableApps);
    }
  };
  const chooseAccounts = async () => {
    logBI(biCodes.chooseAccounts, {
      vendorId,
      userId
    });
    if (isAccountingQbo(accounting)) {
      reportToHeap('integrations', 'click', 'qbo-choose-accounts');
      reportToHubspot('vendor_started_contact_sync');
    }
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/_jsx(MapAccountsModal, {
          dismissModal: dismissModal,
          accounting: accounting
        });
      }
    });
  };
  const mapContacts = async () => {
    logBI(biCodes.mapContacts, {
      vendorId,
      userId
    });
    if (isAccountingQbo(accounting)) {
      reportToAppcues('button-click', 'qbo-vendor-started-contacts-map');
      reportToGtm('button-click', 'qbo-vendor-started-contacts-map', {
        vendorId,
        userId
      });
      reportToHeap('integrations', 'click', 'qbo-map-contacts');
      reportToHubspot('vendor_started_contact_sync');
    }
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/_jsx(MapContactsModal, {
          dismissModal: dismissModal,
          accounting: accounting
        });
      }
    });
  };
  const mapServices = async () => {
    logBI(biCodes.mapServices, {
      vendorId,
      userId
    });
    if (isAccountingQbo(accounting)) {
      reportToAppcues('button-click', 'qbo-vendor-started-services-map');
      reportToGtm('button-click', 'qbo-vendor-started-services-map', {
        vendorId,
        userId
      });
      reportToHeap('integrations', 'click', 'qbo-map-services');
      reportToHubspot('vendor_started_services_sync');
    }
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/_jsx(MapServicesModal, {
          dismissModal: dismissModal,
          accounting: accounting
        });
      }
    });
  };
  return /*#__PURE__*/_jsx(Page, {
    "data-testid": "".concat(testIdPrefix, "-setup-page"),
    header: /*#__PURE__*/_jsx("div", {
      className: styles.pageHeader,
      children: title
    }),
    subheader: /*#__PURE__*/_jsxs("div", {
      className: styles.pageSubHeader,
      children: [subheader, /*#__PURE__*/_jsx(Typography, _objectSpread(_objectSpread({
        as: "a",
        href: helpArticle.setup
      }, openInNewTabProps), {}, {
        children: "Learn more"
      }))]
    }),
    loading: isLoading,
    onClickBack: () => {
      if (history.length > 1) {
        history.goBack();
      } else {
        history.push(routes.availableApps);
      }
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.pageContainer,
      children: [(accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.isMultipleAccountsConnected()) && /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": "multiple-accounts-callout",
        withAction: /*#__PURE__*/_jsx(Button, {
          "data-testid": "multiple-accounts-callout-button",
          skin: "primaryOutline",
          onClick: () => history.push(routes.businessDetails),
          children: "Go to Billing Settings"
        }),
        children: /*#__PURE__*/_jsxs(Typography, {
          "data-testid": "multiple-accounts-callout-text",
          children: ["This ", accountingFullName, " account is already linked to another Anchor account. To avoid sync issues, we recommend setting a unique invoice prefix for this account in Billing Settings."]
        })
      }), /*#__PURE__*/_jsxs(Section, {
        testid: "connection-settings",
        header: "Connection settings",
        subHeader: subHeader,
        isError: !accountingIntegration || accountingIntegration.isErrored(),
        children: [/*#__PURE__*/_jsx(GridItemLeft, {
          children: /*#__PURE__*/_jsxs(Typography, {
            children: ["Select which ", accountingName, " accounts will be used"]
          })
        }), /*#__PURE__*/_jsx(GridItemRight, {
          children: /*#__PURE__*/_jsx(Button, {
            className: styles.button,
            "data-testid": "choose-accounts",
            intent: Intent.AccountingMapAccounts,
            onClick: chooseAccounts,
            disabled: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.isErrored(),
            children: "Choose accounts"
          })
        }), isLinked && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(GridItemLeft, {
            children: /*#__PURE__*/_jsxs(Typography, {
              children: ["Map Anchor contacts to ", accountingName, " customers"]
            })
          }), /*#__PURE__*/_jsx(GridItemRight, {
            children: /*#__PURE__*/_jsx(ConditionalWrapper, {
              condition: isPendingAccountingIntegrationSetup,
              wrapper: children => /*#__PURE__*/_jsx(Tooltip, {
                title: /*#__PURE__*/_jsx("div", {
                  "data-testid": "missing-account-mapping-error-contacts",
                  children: "To map contacts, map accounts first"
                }),
                children: children
              }),
              children: /*#__PURE__*/_jsx("span", {
                "data-testid": "map-contacts-button",
                children: /*#__PURE__*/_jsx(Button, {
                  className: styles.button,
                  disabled: isPendingAccountingIntegrationSetup,
                  "data-testid": "map-contacts",
                  intent: Intent.AccountingSetupMapContacts,
                  onClick: mapContacts,
                  children: "Map contacts"
                })
              })
            })
          }), /*#__PURE__*/_jsx(GridItemLeft, {
            children: /*#__PURE__*/_jsxs(Typography, {
              children: ["Map Anchor services to ", accountingName, " services"]
            })
          }), /*#__PURE__*/_jsx(GridItemRight, {
            children: /*#__PURE__*/_jsx(ConditionalWrapper, {
              condition: isPendingAccountingIntegrationSetup,
              wrapper: children => /*#__PURE__*/_jsx(Tooltip, {
                title: /*#__PURE__*/_jsx("div", {
                  "data-testid": "missing-account-mapping-error-services",
                  children: "To map services, map accounts first"
                }),
                children: children
              }),
              children: /*#__PURE__*/_jsx("span", {
                "data-testid": "map-services-button",
                children: /*#__PURE__*/_jsx(Button, {
                  className: styles.button,
                  disabled: isPendingAccountingIntegrationSetup,
                  "data-testid": "map-services",
                  intent: Intent.AccountingMapServices,
                  onClick: mapServices,
                  children: "Map services"
                })
              })
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(Section, {
        testid: "advanced-settings",
        header: "Advanced settings",
        children: /*#__PURE__*/_jsx(AccountingAdvancedSettings, {
          accounting: accounting,
          isLinked: isLinked || false
        })
      }), /*#__PURE__*/_jsxs(Section, {
        testid: "disconnect-".concat(testIdPrefix),
        children: [/*#__PURE__*/_jsx(GridItemLeft, {
          children: /*#__PURE__*/_jsxs(Typography, {
            skin: "error",
            children: ["Disconnect from ", accountingFullName]
          })
        }), /*#__PURE__*/_jsx(GridItemRight, {
          children: /*#__PURE__*/_jsx(Button, {
            className: styles.button,
            skin: "danger",
            "data-testid": "disconnect",
            intent: Intent.AccountingDisconnect,
            onClick: disconnect,
            loading: isDisconnecting,
            disabled: !!accountingIntegration && accountingIntegration.isErrored(),
            children: "Disconnect"
          })
        })]
      })]
    })
  });
}
const getSubHeaderByIntegration = integration => {
  if (integration && integration.isErrored()) return /*#__PURE__*/_jsxs(_Fragment, {
    children: ["Failed to connect. Please ", /*#__PURE__*/_jsx(ContactSupportButton, {
      skin: "error",
      size: "small"
    }), "."]
  });
  return '';
};
const Section = _ref6 => {
  let {
    testid,
    header,
    subHeader,
    isError,
    children
  } = _ref6;
  return /*#__PURE__*/_jsxs(Card, {
    "data-testid": "".concat(testid, "-section"),
    className: styles.section,
    children: [header && /*#__PURE__*/_jsxs("div", {
      className: styles.header,
      children: [/*#__PURE__*/_jsx(Typography, {
        "data-testid": "".concat(testid, "-header"),
        weight: "normal",
        variant: "h5",
        children: header
      }), /*#__PURE__*/_jsx(Typography, {
        skin: isError ? 'error' : 'secondary',
        variant: "paragraph2",
        "data-testid": "".concat(testid, "-sub-header"),
        children: subHeader
      })]
    }), /*#__PURE__*/_jsx(Grid, {
      align: "center",
      rowGap: "small",
      children: children
    })]
  });
};
const GridItemRight = _ref7 => {
  let {
    children
  } = _ref7;
  return /*#__PURE__*/_jsx(GridItem, {
    span: 2,
    justify: "center",
    children: children
  });
};
const GridItemLeft = _ref8 => {
  let {
    children
  } = _ref8;
  return /*#__PURE__*/_jsx(GridItem, {
    span: 10,
    children: children
  });
};